import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import reportLinksService from "../component/features/admin/admin-panel/services/report-links-service.js";

export const ReportLinksContext = createContext(null);

const SIDEBAR_LIST_QUERY = {
  page: 1,
  pageSize: 100,
  isActive: "true",
  orderBy: "name",
  orderDirection: "asc",
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
export function ReportLinksProvider({ children }) {
  const [reportLinks, setReportLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReportLinks = useCallback(async () => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      setReportLinks([]);
      return { success: true, data: [] };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await reportLinksService.list(SIDEBAR_LIST_QUERY);

      if (response.success !== false) {
        const data = Array.isArray(response.data) ? response.data : [];
        setReportLinks(data);
        return { success: true, data };
      }

      setReportLinks([]);
      setError(response.message || "Erro ao carregar links de relatório");
      return { success: false, message: response.message };
    } catch (err) {
      setReportLinks([]);
      setError(err.message || "Erro ao carregar links de relatório");
      return { success: false, message: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportLinks();
  }, [fetchReportLinks]);

  const value = useMemo(
    () => ({
      reportLinks,
      isLoading,
      error,
      refetchReportLinks: fetchReportLinks,
    }),
    [reportLinks, isLoading, error, fetchReportLinks],
  );

  return (
    <ReportLinksContext.Provider value={value}>
      {children}
    </ReportLinksContext.Provider>
  );
}

/**
 * @returns {{
 *   reportLinks: Object[],
 *   isLoading: boolean,
 *   error: string|null,
 *   refetchReportLinks: () => Promise<Object>
 * }}
 */
export function useReportLinks() {
  const context = useContext(ReportLinksContext);
  if (!context) {
    throw new Error("useReportLinks must be used within ReportLinksProvider");
  }
  return context;
}
